import { Card, Tabs } from "@hellocarbo/lasagne";
import {
  BarChart2,
  ClipboardCopy,
  Cross,
  GitFork,
  Import,
  LanguagesIcon,
  Users,
} from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";
import { data, useLoaderData } from "react-router";

import { NavigationMenuLinks } from "@/components/NavigationMenu";
import OptionItem from "@/components/Option/OptionItem";
import ProServicesContent from "@/components/Option/ProServicesContent";
import { UserContext } from "@/contexts";
import { CustomUIMatch } from "@/hooks/useBreadcrumbs";

export const handle = {
  activeMenuLink: () => NavigationMenuLinks.options,
  breadcrumb: () => [{ href: "/options", label: "options:title" }],
} as CustomUIMatch["handle"];

enum OPTIONS_VIEWS {
  byOptions = "options",
  byProServices = "services",
}

export async function loader() {
  const optionsResponse = await fetch(`/api/client/options`);
  if (optionsResponse.status !== 200) {
    throw data("Failed to load the options", {
      status: optionsResponse.status,
    });
  }
  const { data: optionsData } = (await optionsResponse.json()) as {
    data: Array<{
      key: string;
    }>;
  };

  const keys = optionsData?.map((option: { key: string }) => option.key);

  return {
    options: keys,
  };
}

const iconMap = {
  simulation: BarChart2,
  user: Users,
  additional_import: Import,
  multi_entity: GitFork,
  external_campaign: ClipboardCopy,
  team_score_translation: LanguagesIcon,
  additional_score: Cross,
};

const OPTIONS_WITHOUT_CTA = ["additional_score"];

function Options() {
  const { t } = useTranslation("options");
  const { options } = useLoaderData<typeof loader>();
  const { user } = use(UserContext);

  const canCreateSimulation = user?.policies?.simulation.queries["create"];

  return (
    <Card>
      <Tabs
        defaultValue={OPTIONS_VIEWS.byOptions}
        tabs={[
          {
            value: OPTIONS_VIEWS.byOptions,
            label: t("options:byOptions.title"),
            content: (
              <ul className="gap-sm grid grid-cols-1 xl:grid-cols-2">
                {options.map((optionKey: string) => {
                  if (optionKey === "simulation" && canCreateSimulation) {
                    return null;
                  }
                  const icon = iconMap[optionKey as keyof typeof iconMap];
                  const shouldNotDisplayCTA =
                    OPTIONS_WITHOUT_CTA.includes(optionKey);

                  return (
                    <li key={optionKey}>
                      <OptionItem
                        optionKey={optionKey}
                        title={t(`options:optionsList.${optionKey}.title`)}
                        description={t(
                          `options:optionsList.${optionKey}.description`
                        )}
                        href={"/options/subscription"}
                        tag={t(`options:optionsList.${optionKey}.tag`)}
                        Icon={icon}
                        shouldNotDisplayCTA={shouldNotDisplayCTA}
                      />
                    </li>
                  );
                })}
              </ul>
            ),
          },
          {
            value: OPTIONS_VIEWS.byProServices,
            label: t("options:byProServices.title"),
            content: <ProServicesContent />,
          },
        ]}
      />
    </Card>
  );
}

export default Options;
